import React, { useMemo } from "react";
import { Marker, InfoWindow } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { Button, Typography, Tag, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { Text } = Typography;

export type OrderMarkerStatus = "assigned" | "unassigned" | "recommended" | "selected";

export interface IOrderMarkerData {
  id: number;
  number: string;
  clientName?: string;
  clientAddress?: string;
  sequence?: number;
  distanceToRoute?: number;
  status?: string;
}

interface OrderMarkerProps {
  position: google.maps.LatLngLiteral;
  order: IOrderMarkerData;
  markerStatus: OrderMarkerStatus;
  sequence?: number;
  isSelected?: boolean;
  isAssigned?: boolean; // Whether the order is actually in the trip
  showInfoWindow?: boolean;
  onSelect?: () => void;
  onAdd?: () => void;
  onRemove?: () => void;
  onCloseInfoWindow?: () => void;
  zIndex?: number;
}

/**
 * Generate SVG marker icon with number
 */
const createNumberedMarkerIcon = (
  sequence: number | undefined,
  markerStatus: OrderMarkerStatus,
  isSelected: boolean
): string => {
  // Color scheme based on status
  const colors: Record<OrderMarkerStatus, { bg: string; border: string }> = {
    assigned: { bg: "#3458a4", border: "#283f7a" },
    unassigned: { bg: "#8c8c8c", border: "#595959" },
    recommended: { bg: "#52c41a", border: "#389e0d" },
    selected: { bg: "#fa8c16", border: "#d46b08" },
  };

  const color = isSelected ? colors.selected : colors[markerStatus];
  const displayText = sequence !== undefined ? String(sequence) : "";
  const fontSize = displayText.length > 1 ? "16" : "18";

  // Larger marker size: 56x70 (with padding for stroke/shadow)
  const svg = `
    <svg width="56" height="70" viewBox="-3 -3 56 70" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="3" flood-opacity="0.4"/>
        </filter>
      </defs>
      <path d="M25 0C11.19 0 0 11.19 0 25C0 43.75 25 62 25 62S50 43.75 50 25C50 11.19 38.81 0 25 0Z"
            fill="${color.bg}"
            stroke="${color.border}"
            stroke-width="2"
            filter="url(#shadow)"/>
      <circle cx="25" cy="22" r="14" fill="white"/>
      ${displayText ? `<text x="25" y="28" text-anchor="middle" font-size="${fontSize}" font-weight="bold" fill="${color.bg}">${displayText}</text>` : ""}
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Create unassigned/available marker icon (medium size)
 */
const createSmallMarkerIcon = (
  markerStatus: OrderMarkerStatus,
  isSelected: boolean
): string => {
  const colors: Record<OrderMarkerStatus, string> = {
    assigned: "#3458a4",
    unassigned: "#8c8c8c",
    recommended: "#52c41a",
    selected: "#fa8c16",
  };

  const color = isSelected ? colors.selected : colors[markerStatus];

  // Medium marker size: 40x50 (with padding for shadow)
  const svg = `
    <svg width="40" height="50" viewBox="-2 -2 40 50" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow2" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
        </filter>
      </defs>
      <path d="M18 0C8.06 0 0 8.06 0 18C0 31.5 18 45 18 45S36 31.5 36 18C36 8.06 27.94 0 18 0Z"
            fill="${color}"
            filter="url(#shadow2)"/>
      <circle cx="18" cy="16" r="7" fill="white"/>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * OrderMarker Component
 * Displays an order location on the map with visual indicators
 */
const OrderMarker: React.FC<OrderMarkerProps> = ({
  position,
  order,
  markerStatus,
  sequence,
  isSelected = false,
  isAssigned = false,
  showInfoWindow = false,
  onSelect,
  onAdd,
  onRemove,
  onCloseInfoWindow,
  zIndex = 10,
}) => {
  const { t } = useTranslation();

  const icon = useMemo(() => {
    if (markerStatus === "assigned" && sequence !== undefined) {
      return {
        url: createNumberedMarkerIcon(sequence, markerStatus, isSelected),
        scaledSize: new window.google.maps.Size(56, 70),
        anchor: new window.google.maps.Point(28, 67),
      };
    }
    return {
      url: createSmallMarkerIcon(markerStatus, isSelected),
      scaledSize: new window.google.maps.Size(40, 50),
      anchor: new window.google.maps.Point(20, 47),
    };
  }, [markerStatus, sequence, isSelected]);

  const animation = isSelected
    ? window.google?.maps?.Animation?.BOUNCE
    : undefined;

  const markerZIndex = useMemo(() => {
    if (isSelected) return 1000;
    if (markerStatus === "assigned") return 100 + (sequence || 0);
    if (markerStatus === "recommended") return 50;
    return zIndex;
  }, [isSelected, markerStatus, sequence, zIndex]);

  // Trip status tag - shows if order is in trip or available
  // Uses isAssigned prop to correctly show status even when marker is selected
  const tripStatusTag = useMemo(() => {
    if (isAssigned || markerStatus === "assigned") {
      return { color: "#3458a4", label: t("in_trip") };
    }
    if (markerStatus === "recommended") {
      return { color: "#52c41a", label: t("recommended") };
    }
    return { color: "#8c8c8c", label: t("available_order") };
  }, [isAssigned, markerStatus, t]);

  return (
    <>
      <Marker
        position={position}
        icon={icon}
        onClick={onSelect}
        animation={animation}
        zIndex={markerZIndex}
        title={`#${order.number}${order.clientName ? ` - ${order.clientName}` : ""}`}
      />

      {showInfoWindow && (
        <InfoWindow position={position} onCloseClick={onCloseInfoWindow}>
          <div style={{ minWidth: 200, maxWidth: 280 }}>
            {/* Order number header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
                paddingBottom: 8,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <Text strong style={{ fontSize: 16 }}>
                #{order.number}
              </Text>
              <Tag
                style={{
                  backgroundColor: tripStatusTag.color,
                  color: "#fff",
                  border: "none",
                  fontWeight: 500,
                  margin: 0,
                  fontSize: 11,
                  padding: "2px 8px",
                }}
              >
                {tripStatusTag.label}
              </Tag>
            </div>

            {/* Client info */}
            {order.clientName && (
              <div style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: 13, color: "#262626" }}>
                  {order.clientName}
                </Text>
              </div>
            )}

            {order.clientAddress && (
              <div style={{ marginBottom: 6 }}>
                <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                  {order.clientAddress}
                </Text>
              </div>
            )}

            {order.distanceToRoute !== undefined && (
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                  {t("distance_to_route")}: {order.distanceToRoute.toFixed(2)} {t("km")}
                </Text>
              </div>
            )}

            {/* Action button */}
            {((!isAssigned && onAdd) || (isAssigned && onRemove)) && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f0f0f0" }}>
                {!isAssigned && onAdd && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    block
                    style={{
                      backgroundColor: markerStatus === "recommended" ? "#52c41a" : "#3458a4",
                      borderColor: markerStatus === "recommended" ? "#52c41a" : "#3458a4",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdd();
                    }}
                  >
                    {t("add_to_trip")}
                  </Button>
                )}

                {isAssigned && onRemove && (
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    block
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                  >
                    {t("remove")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default React.memo(OrderMarker);
